/**
 * 통합 검색 서비스
 * 패스워드, 신용카드, API 키를 통합하여 검색하고 관리
 */

import passwordService from './passwordService';
import { getCreditCards } from './creditCardService';
import { getApiKeys } from './apiKeyService';

/**
 * 모든 타입의 데이터를 통합 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Object>} 통합 데이터
 */
export async function getAllItems(userId) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 통합 데이터 조회 중...');
    }

    // 병렬로 모든 데이터 조회
    const [passwords, creditCards, apiKeys] = await Promise.all([
      passwordService.getPasswords(userId).catch(() => []),
      getCreditCards(userId).catch(() => []),
      getApiKeys(userId).catch(() => [])
    ]);

    // 통합 배열 생성
    const allItems = [
      ...passwords.map(item => ({ ...item, itemType: 'password' })),
      ...creditCards.map(item => ({ ...item, itemType: 'creditcard' })),
      ...apiKeys.map(item => ({ ...item, itemType: 'apikey' }))
    ];

    // 최신 순으로 정렬
    allItems.sort((a, b) => {
      const dateA = (a.updatedAt || a.createdAt)?.toDate?.() || new Date(0);
      const dateB = (b.updatedAt || b.createdAt)?.toDate?.() || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ 통합 데이터 ${allItems.length}개 조회 완료`);
    }

    return {
      allItems,
      passwords,
      creditCards,
      apiKeys,
      stats: {
        totalItems: allItems.length,
        passwords: passwords.length,
        creditCards: creditCards.length,
        apiKeys: apiKeys.length
      }
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ 통합 데이터 조회 실패:', error);
    }
    throw new Error('데이터를 조회할 수 없습니다.');
  }
}

/**
 * 통합 검색
 * @param {string} userId - 사용자 ID
 * @param {string} searchTerm - 검색어
 * @param {Object} filters - 필터 옵션
 * @returns {Promise<Array>} 검색 결과
 */
export async function searchAllItems(userId, searchTerm, filters = {}) {
  try {
    const { allItems } = await getAllItems(userId);
    
    let results = allItems;

    // 타입 필터링
    if (filters.types && filters.types.length > 0) {
      results = results.filter(item => filters.types.includes(item.itemType));
    }

    // 태그 필터링
    if (filters.tags && filters.tags.length > 0) {
      results = results.filter(item => 
        item.tags && item.tags.some(tag => filters.tags.includes(tag))
      );
    }

    // 즐겨찾기 필터링
    if (filters.favoritesOnly) {
      results = results.filter(item => item.favorite);
    }

    // 검색어 필터링
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(item => {
        // 공통 필드 검색
        if (item.title?.toLowerCase().includes(searchLower)) return true;
        if (item.note?.toLowerCase().includes(searchLower)) return true;
        if (item.notes?.toLowerCase().includes(searchLower)) return true;
        if (item.tags?.some(tag => tag.toLowerCase().includes(searchLower))) return true;

        // 타입별 특수 필드 검색
        switch (item.itemType) {
          case 'password':
            return item.username?.toLowerCase().includes(searchLower) ||
                   item.website?.toLowerCase().includes(searchLower);
          
          case 'creditcard':
            return item.cardName?.toLowerCase().includes(searchLower) ||
                   item.cardHolder?.toLowerCase().includes(searchLower) ||
                   item.cardholderName?.toLowerCase().includes(searchLower) ||
                   item.bank?.toLowerCase().includes(searchLower) ||
                   (item.cardNumber && item.cardNumber.slice(-4).includes(searchLower));
          
          case 'apikey':
            return item.keyName?.toLowerCase().includes(searchLower) ||
                   item.provider?.toLowerCase().includes(searchLower) ||
                   item.environment?.toLowerCase().includes(searchLower);
          
          default:
            return false;
        }
      });
    }

    return results;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ 통합 검색 실패:', error);
    }
    return [];
  }
}

/**
 * 아이템 타입별 아이콘 반환
 * @param {string} itemType - 아이템 타입
 * @returns {string} 아이콘
 */
export function getItemTypeIcon(itemType) {
  const icons = {
    password: '🔐',
    creditcard: '💳',
    apikey: '🔑'
  };

  return icons[itemType] || '📋';
}

/**
 * 아이템 타입별 라벨 반환
 * @param {string} itemType - 아이템 타입
 * @returns {string} 라벨
 */
export function getItemTypeLabel(itemType) {
  const labels = {
    password: '패스워드',
    creditcard: '신용카드',
    apikey: 'API 키'
  };

  return labels[itemType] || '항목';
}

/**
 * 아이템 타입별 색상 반환
 * @param {string} itemType - 아이템 타입
 * @returns {string} 색상 클래스
 */
export function getItemTypeColorClass(itemType) {
  const colors = {
    password: 'item-type-password',
    creditcard: 'item-type-creditcard',
    apikey: 'item-type-apikey'
  };

  return colors[itemType] || 'item-type-default';
}

/**
 * 모든 태그 추출
 * @param {Array} items - 아이템 배열
 * @returns {Array} 고유 태그 목록
 */
export function extractAllTags(items) {
  const tagSet = new Set();
  
  items.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => tagSet.add(tag));
    }
  });

  return Array.from(tagSet).sort();
}

/**
 * 보안 점수 계산
 * @param {Object} stats - 통계 정보
 * @param {Array} items - 아이템 배열
 * @returns {Object} 보안 점수 정보
 */
export function calculateSecurityScore(stats, items) {
  let score = 100;
  const issues = [];

  // 만료된 카드 체크
  const expiredCards = items.filter(item => 
    item.itemType === 'creditcard' && 
    item.expiryYear && item.expiryMonth &&
    new Date(2000 + parseInt(item.expiryYear), parseInt(item.expiryMonth) - 1) < new Date()
  );

  if (expiredCards.length > 0) {
    score -= expiredCards.length * 5;
    issues.push(`만료된 카드 ${expiredCards.length}개`);
  }

  // 만료된 API 키 체크
  const expiredKeys = items.filter(item => 
    item.itemType === 'apikey' && item.isExpired
  );

  if (expiredKeys.length > 0) {
    score -= expiredKeys.length * 5;
    issues.push(`만료된 API 키 ${expiredKeys.length}개`);
  }

  // 약한 패스워드 체크 (예시)
  const weakPasswords = items.filter(item => 
    item.itemType === 'password' && 
    item.password && item.password.length < 8
  );

  if (weakPasswords.length > 0) {
    score -= weakPasswords.length * 10;
    issues.push(`약한 패스워드 ${weakPasswords.length}개`);
  }

  return {
    score: Math.max(0, score),
    grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F',
    issues
  };
}

export default {
  getAllItems,
  searchAllItems,
  getItemTypeIcon,
  getItemTypeLabel,
  getItemTypeColorClass,
  extractAllTags,
  calculateSecurityScore
};